import React, { useState } from 'react';

import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Box,
  Button,
  FormField,
  IconButton,
  InputAdornment,
  PageHeader,
} from 'components';
import { useNotifications } from 'hooks';

import { FieldError, useForm, useSelector, useTranslation } from 'third-party';

import { useUser } from 'modules/user';

import { userSelectors } from 'selectors';

enum PasswordFormFields {
  CurrentPassword = 'currentPassword',
  NewPassword = 'newPassword',
  ConfirmPassword = 'confirmPassword',
}

type PasswordFormData = {
  [PasswordFormFields.CurrentPassword]: string;
  [PasswordFormFields.NewPassword]: string;
  [PasswordFormFields.ConfirmPassword]: string;
};

type Props = {
  onClose: () => void;
};

export const ChangePasswordForm: React.FC<Props> = ({ onClose }) => {
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    mode: 'onSubmit',
    defaultValues: {
      [PasswordFormFields.CurrentPassword]: '',
      [PasswordFormFields.NewPassword]: '',
      [PasswordFormFields.ConfirmPassword]: '',
    },
  });

  const { t } = useTranslation();
  const { changeUserPassword } = useUser();
  const { success, error } = useNotifications();
  const changeUserPasswordPending = useSelector(
    userSelectors.changeUserPasswordPending,
  );

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const isFormPending = changeUserPasswordPending;

  const onSubmit = async (data: PasswordFormData) => {
    const result = await changeUserPassword(
      data.currentPassword,
      data.newPassword,
    );

    if (result.error) {
      error(t('userProfile.changePassword.error'));
      return;
    }
    success(t('userProfile.changePassword.success'));
    onClose();
  };

  return (
    <Box margin="1rem" display="flex" flexDirection="column" gap={'1rem'}>
      <PageHeader
        header={t('userProfile.password.header')}
        subHeader={t('userProfile.password.subHeader')}
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormField
          type={showCurrentPassword ? 'text' : 'password'}
          fieldName={PasswordFormFields.CurrentPassword}
          label={t('userProfile.currentPassword')}
          placeholder={
            t('common.form.enterValueFor') + t('userProfile.currentPassword')
          }
          requiredErrorMessage={t('common.form.fieldRequired')}
          control={control}
          maxLength={200}
          isRequired
          isDisabled={isFormPending}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  sx={{ padding: 0 }}
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <FormField
          type={showNewPassword ? 'text' : 'password'}
          fieldName={PasswordFormFields.NewPassword}
          fieldError={errors[PasswordFormFields.NewPassword] as FieldError}
          label={t('userProfile.newPassword')}
          placeholder={
            t('common.form.enterValueFor') + t('userProfile.newPassword')
          }
          requiredErrorMessage={t('common.form.fieldRequired')}
          rules={{
            validate: {
              incorrectPasswords: (value: string, formValues: any) =>
                value === formValues[PasswordFormFields.ConfirmPassword] ||
                t('userProfile.changePassword.passwordsDoNotMatch'),
            },
          }}
          control={control}
          maxLength={200}
          isRequired
          isDisabled={isFormPending}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  sx={{ padding: 0 }}
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <FormField
          type={showConfirmPassword ? 'text' : 'password'}
          fieldName={PasswordFormFields.ConfirmPassword}
          fieldError={errors[PasswordFormFields.ConfirmPassword] as FieldError}
          label={t('userProfile.confirmPassword')}
          placeholder={
            t('common.form.enterValueTo') + t('userProfile.confirmPassword')
          }
          requiredErrorMessage={t('common.form.fieldRequired')}
          control={control}
          maxLength={200}
          isRequired
          rules={{
            validate: {
              incorrectPasswords: (value: string, formValues: any) =>
                value === formValues[PasswordFormFields.NewPassword] ||
                t('userProfile.changePassword.passwordsDoNotMatch'),
            },
          }}
          isDisabled={isFormPending}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  sx={{ padding: 0 }}
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Box display="flex" justifyContent="space-between" mt="1rem">
          <Button
            type="button"
            variant="outlined"
            onClick={onClose}
            size="medium"
            color="error"
            disabled={isFormPending}
          >
            {t('common.form.cancel')}
          </Button>
          <Button
            type="submit"
            variant="outlined"
            size="medium"
            color="primary"
            disabled={isFormPending}
          >
            {t('common.form.save')}
          </Button>
        </Box>
      </form>
    </Box>
  );
};
